import { PubSubService } from '@ohif/core';
import {
  ORGANS,
  OrganEnum,
  OrganQualityEnum,
  OrganReview,
  OrganReviewStatusEnum,
  OrganReviewSubmission,
  fetchOrganReviews,
  submitOrganReviews,
} from '../clients/viewer';
import { UnauthorizedException } from '../http';
import { describeError } from '../utils';

const EVENTS = {
  ORGAN_REVIEWS_CHANGED: 'event::sycai:OrganReviewService:organReviewsChanged',
  SESSION_LOST: 'event::sycai:OrganReviewService:sessionLost',
} as const;

export type OrganReviewEventPayloads = {
  [EVENTS.ORGAN_REVIEWS_CHANGED]: { studyInstanceUid: string; seriesInstanceUid: string };
  [EVENTS.SESSION_LOST]: { studyInstanceUid: string; seriesInstanceUid: string };
};

export type OrganRowState = 'unissued' | 'gradeable' | 'inProgress' | 'complete' | 'failed';

/**
 * §5.6 consequence 1. develop encoded these as `status?` plus `quality?` plus an absent
 * key, which is what let defects 1 and 9 coexist. Discriminating on the state makes the
 * renderer's table exhaustive, and puts `reviewId` on exactly the four states that have
 * one — so a row the server has not issued cannot be submitted, by type.
 */
export type OrganRow =
  | { organ: OrganEnum; state: 'unissued' }
  | {
      organ: OrganEnum;
      state: Exclude<OrganRowState, 'unissued'>;
      reviewId: string;
      quality?: OrganQualityEnum;
    };

interface OrganEntry {
  reviewId: string;
  status?: OrganReviewStatusEnum;
  quality?: OrganQualityEnum;
  /** True while `quality` is a local choice the server has not been told about (D7). */
  isLocalQuality: boolean;
  /**
   * Plan D-15. The server has stopped issuing this organ, but a local grade is held so it
   * returns if the organ does. The row renders as unissued: its reviewId is stale, so it
   * must not be submittable, and it must not be able to hold a status.
   */
  isOrphaned: boolean;
}

type SeriesEntries = Map<OrganEnum, OrganEntry>;

const NO_ENTRIES: SeriesEntries = new Map();

function toRow(organ: OrganEnum, entry: OrganEntry | undefined): OrganRow {
  if (!entry || entry.isOrphaned) {
    return { organ, state: 'unissued' };
  }

  const { reviewId, status, quality } = entry;

  if (status === OrganReviewStatusEnum.IN_PROGRESS) {
    return { organ, state: 'inProgress', reviewId, quality };
  }

  if (status === OrganReviewStatusEnum.COMPLETED) {
    return { organ, state: 'complete', reviewId, quality };
  }

  if (status === OrganReviewStatusEnum.FAILED) {
    return { organ, state: 'failed', reviewId, quality };
  }

  // NOT_FOUND and an absent status both mean "issued, never graded", the same routing
  // fetchDiagnosisStatus gives NOT_FOUND (clients/viewer.ts:55-66).
  return { organ, state: 'gradeable', reviewId, quality };
}

/**
 * D8: a FAILED organ is re-submittable; D6 governs COMPLETED only, and the user
 * confirmed on 2026-08-12 that a reviewer does not revise a submitted grade.
 */
export function isSubmittable(
  row: OrganRow
): row is Extract<OrganRow, { reviewId: string }> & { quality: OrganQualityEnum } {
  return (row.state === 'gradeable' || row.state === 'failed') && row.quality !== undefined;
}

export class OrganReviewService extends PubSubService {
  public static readonly EVENTS = EVENTS;

  public static readonly REGISTRATION = {
    name: 'organReviewService',
    create: () => new OrganReviewService(),
  };

  private studies = new Map<string, Map<string, SeriesEntries>>();

  /** Plan D-4. Per series, because submit() races the poller's refresh(). */
  private generations = new Map<string, number>();

  /**
   * Plan D-4, second half. A generation counter cannot express this: the optimistic
   * IN_PROGRESS write ARMS the poll that then races it, so a tick issued after the write
   * carries the current generation and is not stale — it would roll the row back to
   * gradeable, stop polling, and re-enable Submit while the POST is still out.
   */
  private submitting = new Set<string>();

  /** Plan D-13. Series whose last read failed, so an empty panel can say which it is. */
  private failedReads = new Set<string>();

  /**
   * M4.1's final review verdict, deferred here: refresh() had no in-flight dedupe, so a
   * series change and a panel open landing together issued two GETs.
   * DiagnosisService.ts:193-213 is the precedent, including why the entry clears in a
   * `.finally()` whose identity is re-checked -- a newer generation may have replaced it
   * while this request was out, and deleting THAT one would let the next caller issue a
   * duplicate against a request that is still live.
   */
  private refreshesInFlight = new Map<string, { generation: number; request: Promise<void> }>();

  constructor() {
    super(EVENTS);
  }

  private emit<E extends keyof OrganReviewEventPayloads>(
    event: E,
    payload: OrganReviewEventPayloads[E]
  ): void {
    this._broadcastEvent(event, payload);
  }

  public getRows(studyInstanceUid: string, seriesInstanceUid: string): readonly OrganRow[] {
    const entries = this.entriesOf(studyInstanceUid, seriesInstanceUid);

    // ORGANS, always, in one order. develop iterated Object.values(OrganEnum) in the
    // dialog and a second hardcoded list in the store (useOrganReviewStore.ts:161).
    return ORGANS.map(organ => toRow(organ, entries.get(organ)));
  }

  public hasError(seriesInstanceUid: string): boolean {
    return this.failedReads.has(seriesInstanceUid);
  }

  public getSubmittable(
    studyInstanceUid: string,
    seriesInstanceUid: string
  ): OrganReviewSubmission[] {
    return this.getRows(studyInstanceUid, seriesInstanceUid)
      .filter(isSubmittable)
      .map(({ reviewId, quality }) => ({ reviewId, quality }));
  }

  /**
   * A local choice, held until Submit sends it. D7: "Save & Close" is bare onClose on
   * develop (OrganReviewDialog.tsx:94-100) and the selections survive the panel closing,
   * which is a second reason the state cannot live in component state.
   *
   * `undefined` is a deselection, not a no-op — ToggleGroup passes it when the selected
   * option is clicked again — and it must still set isLocalQuality, or the next refresh
   * would show the server's quality through the hole the reviewer just made.
   */
  public setQuality(
    studyInstanceUid: string,
    seriesInstanceUid: string,
    organ: OrganEnum,
    quality?: OrganQualityEnum
  ): void {
    const entries = this.mutableEntriesOf(studyInstanceUid, seriesInstanceUid);
    const entry = entries.get(organ);

    // Unissued rows are not gradeable, and the panel disables them; this is the guard
    // that makes that a property of the service rather than of the renderer.
    if (!entry || entry.isOrphaned || entry.status === OrganReviewStatusEnum.COMPLETED) {
      return;
    }

    entries.set(organ, { ...entry, quality, isLocalQuality: true });
    this.broadcast(studyInstanceUid, seriesInstanceUid);
  }

  /**
   * Never rejects for any error it originates. usePolling's interval body is
   * `void callbackRef.current()` with no .catch() (usePolling.ts:37-39), so a rejection
   * here is an unhandled rejection every tick. A throwing subscriber can still reject it
   * through `emit` -> `_broadcastEvent` (pubSubServiceInterface.ts:83-95 invokes listeners
   * inline, no try/catch) — confirmed unreachable today, since ORGAN_REVIEWS_CHANGED's
   * only subscriber (useOrganReview.ts) does a UID comparison and a `setState`, but that
   * is a property of the subscriber, not a guarantee this method makes.
   *
   * The dedupe is keyed on the SERIES, not the study: the generation counter is per-series
   * (:105) and so is every other latch in this class.
   */
  public async refresh(studyInstanceUid: string, seriesInstanceUid: string): Promise<void> {
    // Plan D-12. generationOf is a `?? 0` READ and does not insert, so without this the
    // counter stays empty until the first submit -- and onModeExit's bump, which iterates it,
    // would evict nothing for a study that was only ever read.
    this.ensureGeneration(seriesInstanceUid);
    const generation = this.generationOf(seriesInstanceUid);
    const inFlight = this.refreshesInFlight.get(seriesInstanceUid);

    // Generation-matched, not merely present: submit() bumps before its own read-back
    // (:282), so that call must NOT join a request issued before the POST landed.
    if (inFlight && inFlight.generation === generation) {
      return inFlight.request;
    }

    const request = this.read(studyInstanceUid, seriesInstanceUid, generation).finally(() => {
      if (this.refreshesInFlight.get(seriesInstanceUid)?.request === request) {
        this.refreshesInFlight.delete(seriesInstanceUid);
      }
    });

    this.refreshesInFlight.set(seriesInstanceUid, { generation, request });

    return request;
  }

  /**
   * D8: a failed fetch leaves the map alone rather than emptying it. develop returned [] on
   * any failure and re-seeded the map from it.
   */
  private async read(
    studyInstanceUid: string,
    seriesInstanceUid: string,
    generation: number
  ): Promise<void> {
    try {
      const reviews = await fetchOrganReviews(seriesInstanceUid);

      if (this.isStale(seriesInstanceUid, generation)) {
        return;
      }

      this.failedReads.delete(seriesInstanceUid);
      this.merge(studyInstanceUid, seriesInstanceUid, reviews);
    } catch (error) {
      if (this.isStale(seriesInstanceUid, generation)) {
        return;
      }

      // Plan D-13. Recorded, not just logged: three unissued rows and a 500 render
      // identically, and the panel has to be able to tell the reviewer which it is. The
      // broadcast is what lets the hook stop polling -- develop's catch signalled nobody, so
      // a row stuck inProgress against a failing endpoint polled for the whole mode.
      this.failedReads.add(seriesInstanceUid);
      this.report(studyInstanceUid, seriesInstanceUid, 'reading the organ reviews', error);
      this.broadcast(studyInstanceUid, seriesInstanceUid);
    }
  }

  /**
   * Rejects on a failed POST, so the hook can raise the failure notification (plan D-6).
   * Rolls back from a snapshot taken before the optimistic write.
   *
   * develop's rollback was a no-op by construction (§6 defect 2): its catch re-derived
   * the revert list from getReviewsToSubmit, whose filter at useOrganReviewStore.ts:175
   * excludes IN_PROGRESS — the status the optimistic write had just set on every
   * candidate — so the list was always empty and the button wedged on a spinner until a
   * poll rescued it.
   */
  public async submit(studyInstanceUid: string, seriesInstanceUid: string): Promise<void> {
    const submission = this.getSubmittable(studyInstanceUid, seriesInstanceUid);

    if (submission.length === 0) {
      return;
    }

    const entries = this.mutableEntriesOf(studyInstanceUid, seriesInstanceUid);

    // Shallow is sufficient ONLY because every writer here replaces the entry object
    // rather than mutating it. A later `entry.status = …` would silently defeat this.
    const snapshot = new Map(entries);
    const submitted = new Set(submission.map(({ reviewId }) => reviewId));

    this.bumpGeneration(seriesInstanceUid);
    const generation = this.generationOf(seriesInstanceUid);
    this.submitting.add(seriesInstanceUid);

    // One write, one broadcast. develop called set() once per review inside a forEach
    // (useOrganReviewStore.ts:101-106), producing N renders per submit.
    entries.forEach((entry, organ) => {
      if (submitted.has(entry.reviewId)) {
        entries.set(organ, { ...entry, status: OrganReviewStatusEnum.IN_PROGRESS });
      }
    });

    this.broadcast(studyInstanceUid, seriesInstanceUid);

    try {
      await submitOrganReviews(seriesInstanceUid, submission);
    } catch (error) {
      // Released BEFORE the rollback, so that every mutation of the latch is followed by a
      // broadcast rather than preceded by the last one.
      this.submitting.delete(seriesInstanceUid);

      // Guarded, per plan D-12: restore() goes through mutableEntriesOf, so an unguarded
      // rollback after a mode exit rebuilds both map levels and broadcasts into a
      // torn-down tree. submit() is the path that populates generations, so this is
      // reachable rather than theoretical.
      if (this.isStale(seriesInstanceUid, generation)) {
        this.broadcast(studyInstanceUid, seriesInstanceUid);
      } else {
        this.restore(studyInstanceUid, seriesInstanceUid, snapshot, submitted);
      }

      this.report(studyInstanceUid, seriesInstanceUid, 'submitting the organ reviews', error);
      throw error;
    }

    // The same D-12 guard as the catch above, applied to the success path.
    if (this.isStale(seriesInstanceUid, generation)) {
      this.submitting.delete(seriesInstanceUid);
      return;
    }

    this.bumpGeneration(seriesInstanceUid);

    /**
     * The latch is held ACROSS the read-back, deliberately (plan D-15). A 202-Accepted
     * backend need not report IN_PROGRESS on the very next GET, and releasing first would
     * let that read-back roll the rows straight back to gradeable, stop the poll and
     * re-enable Submit — D-4's failure, moved from during the POST to just after it.
     * merge() still lets a COMPLETED or FAILED answer through, so a fast backend is not
     * held back. Whether the read-back is consistent with the write is a §7.2 question.
     */
    await this.refresh(studyInstanceUid, seriesInstanceUid);

    this.submitting.delete(seriesInstanceUid);
    this.broadcast(studyInstanceUid, seriesInstanceUid);
  }

  /**
   * Domain state is dropped on mode exit and nowhere else — the rule M3's two services
   * follow. develop evicted from the hook's unmount cleanup (useOrganReview.ts:76-82),
   * which a response still in flight resurrects.
   */
  public onModeExit(): void {
    // Bumped, never cleared: clearing puts every counter back to zero and the next submit
    // returns it to one, making a request from the previous mode entry current again
    // (DiagnosisService.ts:433-434). The residue is one integer per series ever seen.
    this.generations.forEach((_, seriesInstanceUid) => this.bumpGeneration(seriesInstanceUid));

    this.studies.clear();
    this.submitting.clear();
    this.failedReads.clear();
    this.refreshesInFlight.clear();
  }

  private entriesOf(studyInstanceUid: string, seriesInstanceUid: string): SeriesEntries {
    return this.studies.get(studyInstanceUid)?.get(seriesInstanceUid) ?? NO_ENTRIES;
  }

  /**
   * Both levels are created here and nowhere else. develop wrote
   * state.reviews[study][series][organ] with no intermediate initialisation
   * (useOrganReviewStore.ts:79-80) and read the same path unguarded from two more places
   * (:89, :155), so any of the three threw before the first successful fetch (§6 defect 4).
   */
  private mutableEntriesOf(studyInstanceUid: string, seriesInstanceUid: string): SeriesEntries {
    let study = this.studies.get(studyInstanceUid);

    if (!study) {
      study = new Map();
      this.studies.set(studyInstanceUid, study);
    }

    let entries = study.get(seriesInstanceUid);

    if (!entries) {
      entries = new Map();
      study.set(seriesInstanceUid, entries);
    }

    return entries;
  }

  /**
   * A local grade the reviewer has not submitted survives the server's view of the row,
   * which is what makes D7's "Save & Close then reopen" work across a poll tick. It does
   * not survive a terminal status: once the server says COMPLETED the row is locked (D6)
   * and the server's quality is the truth.
   */
  private merge(
    studyInstanceUid: string,
    seriesInstanceUid: string,
    reviews: OrganReview[]
  ): void {
    const entries = this.mutableEntriesOf(studyInstanceUid, seriesInstanceUid);
    const previous = new Map(entries);
    const isSubmitting = this.submitting.has(seriesInstanceUid);
    const seen = new Set<OrganEnum>();

    reviews.forEach(({ reviewId, organ, status, quality }) => {
      seen.add(organ);
      const local = previous.get(organ);

      const keepsLocalQuality =
        local?.isLocalQuality === true &&
        local.reviewId === reviewId &&
        status !== OrganReviewStatusEnum.COMPLETED;

      // Plan D-4. A response issued before the POST landed says nothing about a submit
      // that is still in flight, so it may not roll the row back out of IN_PROGRESS —
      // doing so clears the spinner, stops the poll and re-enables Submit mid-request.
      const holdsInProgress =
        isSubmitting &&
        local?.reviewId === reviewId &&
        local.status === OrganReviewStatusEnum.IN_PROGRESS &&
        status !== OrganReviewStatusEnum.COMPLETED &&
        status !== OrganReviewStatusEnum.FAILED;

      entries.set(organ, {
        reviewId,
        status: holdsInProgress ? OrganReviewStatusEnum.IN_PROGRESS : status,
        quality: keepsLocalQuality ? local.quality : quality,
        isLocalQuality: keepsLocalQuality,
        isOrphaned: false,
      });
    });

    /**
     * NOT entries.clear() before the loop. An organ the response omits — because the
     * backend stopped issuing it, or because fetchOrganReviews DROPPED the row for a
     * malformed reviewId — would otherwise take an ungraded reviewer's local choice with
     * it, and come back as `unissued`, which setQuality refuses to write to. The grade
     * would vanish with only a console line to explain it, which is exactly what D7 says
     * must not happen.
     */
    previous.forEach((entry, organ) => {
      if (seen.has(organ)) {
        return;
      }

      if (!entry.isLocalQuality) {
        entries.delete(organ);
        return;
      }

      /**
       * Plan D-15. KEEPING the entry as-is wedges the feature: nothing else clears
       * `isLocalQuality`, so an organ the server has stopped issuing would hold whatever
       * status it had — including IN_PROGRESS — for the life of the mode, and a row stuck
       * inProgress means hasPending is permanently true. The status is dropped and the row
       * is marked orphaned, so it reads as unissued and carries no submittable reviewId,
       * while the grade survives in case the organ comes back.
       */
      entries.set(organ, { ...entry, status: undefined, isOrphaned: true });
    });

    this.broadcast(studyInstanceUid, seriesInstanceUid);
  }

  /**
   * Reverts ONLY the rows the optimistic write touched. Reinstating the whole snapshot —
   * which is what this did before plan D-15 — discards anything learned since it was
   * taken: a grade the reviewer entered on another organ while the POST was outstanding
   * (those rows stay gradeable and enabled), and any organ a poll merge moved to
   * COMPLETED. That is the same loss D-12's merge rewrite exists to prevent, on the
   * rollback path.
   */
  private restore(
    studyInstanceUid: string,
    seriesInstanceUid: string,
    snapshot: SeriesEntries,
    submitted: Set<string>
  ): void {
    const entries = this.mutableEntriesOf(studyInstanceUid, seriesInstanceUid);

    snapshot.forEach((entry, organ) => {
      if (submitted.has(entry.reviewId)) {
        entries.set(organ, entry);
      }
    });

    this.broadcast(studyInstanceUid, seriesInstanceUid);
  }

  private broadcast(studyInstanceUid: string, seriesInstanceUid: string): void {
    this.emit(EVENTS.ORGAN_REVIEWS_CHANGED, { studyInstanceUid, seriesInstanceUid });
  }

  private ensureGeneration(seriesInstanceUid: string): void {
    if (!this.generations.has(seriesInstanceUid)) {
      this.generations.set(seriesInstanceUid, 0);
    }
  }

  private generationOf(seriesInstanceUid: string): number {
    return this.generations.get(seriesInstanceUid) ?? 0;
  }

  private bumpGeneration(seriesInstanceUid: string): void {
    this.generations.set(seriesInstanceUid, this.generationOf(seriesInstanceUid) + 1);
  }

  private isStale(seriesInstanceUid: string, generation: number): boolean {
    return this.generationOf(seriesInstanceUid) !== generation;
  }

  private report(
    studyInstanceUid: string,
    seriesInstanceUid: string,
    action: string,
    error: unknown
  ): void {
    console.error(
      `[sycai] ${action} failed ${describeError(error)}; the panel keeps its last state`
    );

    if (error instanceof UnauthorizedException) {
      this.emit(EVENTS.SESSION_LOST, { studyInstanceUid, seriesInstanceUid });
    }
  }
}

/** See DiagnosisService.ts:517-542 for why this is a declaration merge and an intersection. */
export interface OrganReviewService {
  subscribe: (<E extends keyof OrganReviewEventPayloads>(
    eventName: E,
    callback: (data: OrganReviewEventPayloads[E]) => void
  ) => { unsubscribe: () => void }) &
    PubSubService['subscribe'];
}
